import React from 'react';
import {
    Box,
    Heading,
    Text,
    VStack,
    Divider,
    useColorModeValue,
} from "@chakra-ui/react";

export default function Privacy() {
    const headingColor = useColorModeValue("#f70112", "#ff4757");
    const textColor = useColorModeValue("gray.700", "gray.200");
    const bgColor = useColorModeValue("white", "gray.800");
    
    return (
        <Box
            bg={bgColor}
            borderRadius="lg"
            p={{ base: 4, sm: 6, md: 8 }}
            shadow="sm"
            border="1px"
            borderColor={useColorModeValue("gray.200", "gray.700")}
            mx={{ base: "10px", md: 0 }}
            my={{ base: 2, md: 4 }}
        >
                <VStack spacing={{ base: 6, md: 8 }} align="stretch">
                    <Box textAlign="center">
                        <Heading
                            as="h1"
                            size={{ base: "md", md: "lg" }}
                            color={headingColor}
                            mb={4}
                        >
                            Privacy Policy
                        </Heading>
                        <Text color={textColor} fontSize={{ base: "md", md: "lg" }}>
                            Last updated: {new Date().toLocaleDateString()}
                        </Text>
                    </Box>

                    <Divider />

                    <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                        <Box>
                            <Text color={textColor} lineHeight="1.8" fontSize={{ base: "sm", md: "md" }}>
                                This Privacy Policy outlines our commitment to safeguarding your personal information and ensuring 
                                a secure and trustworthy experience.
                            </Text>
                        </Box>

                        <Box>
                            <Heading as="h2" size={{ base: "sm", md: "md" }} color={headingColor} mb={4}>
                                What This Policy Covers
                            </Heading>
                            <Text color={textColor} lineHeight="1.8" fontSize={{ base: "sm", md: "md" }}>
                                This Privacy Policy applies to information that we collect about you when you use our website 
                                tronweb.io. Throughout this Privacy Policy, we will refer to our website and other products 
                                and services collectively as "Services". Below we explain how we collect, use, and share information 
                                about you, along with the choices that you have with respect to that information.
                            </Text>
                        </Box>

                        <Box>
                            <Heading as="h2" size={{ base: "sm", md: "md" }} color={headingColor} mb={4}>
                                Information We Collect
                            </Heading>
                            <Text color={textColor} lineHeight="1.8" mb={3} fontSize={{ base: "sm", md: "md" }}>
                                We only collect information about you if we have a reason to do so – for example, to provide our 
                                Services, to communicate with you, or to make our Services better. We collect information in two ways: 
                                if and when you provide information to us and automatically through operating our Services.
                            </Text>
                            
                            <Heading as="h3" size="sm" color={headingColor} mb={3} mt={4}>
                                Information You Provide to Us
                            </Heading>
                            <Text color={textColor} lineHeight="1.8" mb={2} fontSize={{ base: "sm", md: "md" }}>
                                It's probably no surprise that we collect information that you provide to us. The amount and type 
                                of information depends on the context and how we use the information. Here are some examples:
                            </Text>
                            <VStack spacing={2} align="stretch" pl={{ base: 3, md: 4 }}>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    <strong>Wallet Address:</strong> We ask wallet public address to retrieve account information 
                                    from TRON blockchain.
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    <strong>Communications With Us:</strong> You may also provide us information when you respond 
                                    to surveys or communicate with our team about a support question.
                                </Text>
                            </VStack>

                            <Heading as="h3" size="sm" color={headingColor} mb={3} mt={4}>
                                Information We Collect Automatically
                            </Heading>
                            <Text color={textColor} lineHeight="1.8" mb={2} fontSize={{ base: "sm", md: "md" }}>
                                We also collect some information automatically:
                            </Text>
                            <VStack spacing={2} align="stretch" pl={{ base: 3, md: 4 }}>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    <strong>Log Information:</strong> Like most online service providers, we collect information that 
                                    web browsers, mobile devices, and servers typically make available, such as the browser type, 
                                    IP address, unique device identifiers, language preference, referring site, the date and time 
                                    of access, operating system, and mobile network information.
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    <strong>Usage Information:</strong> We collect information about your usage of our Services. 
                                    For example, we collect information about the pages you visit or site parts that you check 
                                    the most. We use this information to provide our Services to you, as well as get insights 
                                    on how people use our Services.
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    <strong>Location Information:</strong> We may determine the approximate location of your device 
                                    from your IP address. We collect and use this information to calculate how many people visit 
                                    our Services from certain geographic regions.
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    <strong>Information from Cookies:</strong> A cookie is a string of information that a website 
                                    stores on a visitor's computer, and that the visitor's browser provides to the website each 
                                    time the visitor returns. We use cookies to help us identify and track visitors, usage, and 
                                    access preferences for our Services.
                                </Text>
                            </VStack>
                            
                            <Text color={textColor} lineHeight="1.8" mt={3} fontSize={{ base: "sm", md: "md" }}>
                                And, we will only keep your personal information for as long as it is necessary for the purposes 
                                set out in this privacy policy, unless a longer retention period is required or permitted by law 
                                (such as tax, accounting or other legal requirements).
                            </Text>
                        </Box>

                        <Box>
                            <Heading as="h2" size={{ base: "sm", md: "md" }} color={headingColor} mb={4}>
                                How And Why We Use Information
                            </Heading>
                            
                            <Heading as="h3" size="sm" color={headingColor} mb={3}>
                                Purposes for Using Information
                            </Heading>
                            <Text color={textColor} lineHeight="1.8" mb={2} fontSize={{ base: "sm", md: "md" }}>
                                We use information about you as mentioned above and for the purposes listed below:
                            </Text>
                            <VStack spacing={2} align="stretch" pl={{ base: 3, md: 4 }}>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    • To provide our Services–for example, to set up and maintain your account
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    • To further develop and improve our Services–for example by adding new features that we think 
                                    our users will enjoy or will help them to manage their TRON resources more efficiently
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    • To monitor and analyze trends and better understand how users interact with our Services, 
                                    which helps us improve our Services and make them easier to use
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    • To monitor and prevent any problems with our Services, protect the security of our Services, 
                                    detect and prevent fraudulent transactions and other illegal activities, fight spam, and protect 
                                    our rights and property, which may result in us declining a transaction or the use of our Services
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    • To personalize your experience using our Services
                                </Text>
                            </VStack>

                            <Heading as="h3" size="sm" color={headingColor} mb={3} mt={4}>
                                Legal Bases for Collecting and Using Information
                            </Heading>
                            <Text color={textColor} lineHeight="1.8" mb={2} fontSize={{ base: "sm", md: "md" }}>
                                A note here for those in the European Union about our legal grounds for processing information 
                                about you under EU data protection laws, which is that our use of your information is based on 
                                the grounds that:
                            </Text>
                            <VStack spacing={2} align="stretch" pl={{ base: 3, md: 4 }}>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    • The use is necessary in order to fulfill our commitments to you under our Terms of Service 
                                    or other agreements with you or is necessary to administer your account
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    • The use is necessary for compliance with a legal obligation
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    • The use is necessary in order to protect your vital interests or those of another person
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    • We have a legitimate interest in using your information–for example, to provide and update 
                                    our Services, to improve our Services so that we can offer you an even better user experience, 
                                    to safeguard our Services, to communicate with you, to monitor and prevent any problems with 
                                    our Services, and to personalize your experience
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    • You have given us your consent–for example before we place certain cookies on your device 
                                    and access and analyze them later on
                                </Text>
                            </VStack>
                        </Box>

                        <Box>
                            <Heading as="h2" size={{ base: "sm", md: "md" }} color={headingColor} mb={4}>
                                Sharing Information
                            </Heading>
                            <Text color={textColor} lineHeight="1.8" mb={3} fontSize={{ base: "sm", md: "md" }}>
                                We do not sell our users' private personal information. We share information about you in the 
                                limited circumstances spelled out below and with appropriate safeguards on your privacy:
                            </Text>
                            <VStack spacing={2} align="stretch" pl={{ base: 3, md: 4 }}>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    <strong>Legal Requests:</strong> We may disclose information about you in response to a subpoena, 
                                    court order, or other governmental request.
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    <strong>To Protect Rights, Property, and Others:</strong> We may disclose information about you 
                                    when we believe in good faith that disclosure is reasonably necessary to protect the property 
                                    or rights of ours, third parties, or the public at large.
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    <strong>Business Transfers:</strong> In connection with any merger, sale of company assets, or 
                                    acquisition of all or a portion of our business by another company, or in the unlikely event 
                                    that we go out of business or enters bankruptcy, user information would likely be one of the 
                                    assets that is transferred or acquired by a third party.
                                </Text>
                            </VStack>
                        </Box>

                        <Box>
                            <Heading as="h2" size={{ base: "sm", md: "md" }} color={headingColor} mb={4}>
                                Security
                            </Heading>
                            <Text color={textColor} lineHeight="1.8" fontSize={{ base: "sm", md: "md" }}>
                                While no online service is 100% secure, we work very hard to protect information about you against 
                                unauthorized access, use, alteration, or destruction, and take reasonable measures to do so, such 
                                as monitoring our Services for potential vulnerabilities and attacks.
                            </Text>
                        </Box>

                        <Box>
                            <Heading as="h2" size={{ base: "sm", md: "md" }} color={headingColor} mb={4}>
                                Choices
                            </Heading>
                            <Text color={textColor} lineHeight="1.8" mb={3} fontSize={{ base: "sm", md: "md" }}>
                                You have several choices available when it comes to information about you:
                            </Text>
                            <VStack spacing={2} align="stretch" pl={{ base: 3, md: 4 }}>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    <strong>Set Your Browser to Reject Cookies:</strong> At this time, we do not respond to 
                                    "do not track" signals across all of our Services. However, you can usually choose to set 
                                    your browser to remove or reject browser cookies before using our website, with the drawback 
                                    that certain features may not function properly without the aid of cookies.
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    <strong>Close Your Account:</strong> While we'd be very sad to see you go, if you no longer want 
                                    to use our Services, you can close your account. Please keep in mind that we may continue to 
                                    retain your information after closing your account, as described in Information We Collect above.
                                </Text>
                            </VStack>
                        </Box>

                        <Box>
                            <Heading as="h2" size={{ base: "sm", md: "md" }} color={headingColor} mb={4}>
                                Your Rights
                            </Heading>
                            <Text color={textColor} lineHeight="1.8" mb={3} fontSize={{ base: "sm", md: "md" }}>
                                If you are located in certain countries, including those that fall under the scope of the European 
                                General Data Protection Regulation (AKA the "GDPR"), data protection laws give you rights with respect 
                                to your personal data, subject to any exemptions provided by the law, including the rights to:
                            </Text>
                            <VStack spacing={2} align="stretch" pl={{ base: 3, md: 4 }}>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>• Request access to your personal data</Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>• Request correction or deletion of your personal data</Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>• Object to our use and processing of your personal data</Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>• Request that we limit our use and processing of your personal data</Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>• Request portability of your personal data</Text>
                            </VStack>
                            <Text color={textColor} lineHeight="1.8" mt={3} fontSize={{ base: "sm", md: "md" }}>
                                You can usually access, correct, or delete your personal data using your account settings and tools 
                                that we offer, but if you aren't able to do that, or you would like to contact us about one of the 
                                other rights, scroll down to How to Reach Us to, well, find out how to reach us.
                            </Text>
                            <Text color={textColor} lineHeight="1.8" mt={2} fontSize={{ base: "sm", md: "md" }}>
                                EU individuals also have the right to make a complaint to a government supervisory authority.
                            </Text>
                        </Box>

                        <Box>
                            <Heading as="h2" size={{ base: "sm", md: "md" }} color={headingColor} mb={4}>
                                How to Reach Us
                            </Heading>
                            <Text color={textColor} lineHeight="1.8" fontSize={{ base: "sm", md: "md" }}>
                                If you have a question about this Privacy Policy, or you would like to contact us about any of 
                                the rights mentioned in the Your Rights section above, please contact us through our official 
                                communication channels.
                            </Text>
                        </Box>

                        <Box>
                            <Heading as="h2" size={{ base: "sm", md: "md" }} color={headingColor} mb={4}>
                                Other Things You Should Know
                            </Heading>
                            
                            <Heading as="h3" size="sm" color={headingColor} mb={3}>
                                Transferring Information
                            </Heading>
                            <Text color={textColor} lineHeight="1.8" mb={3} fontSize={{ base: "sm", md: "md" }}>
                                Because our Services are offered worldwide, the information about you that we process when you use 
                                the Services in the EU may be used, stored, and/or accessed by individuals operating outside the 
                                European Economic Area (EEA) who work for us, other members of our group of companies, or third party 
                                data processors. This is required for the purposes listed in the How and Why We Use Information section 
                                above. When providing information about you to entities outside the EEA, we will take appropriate measures 
                                to ensure that the recipient protects your personal information adequately in accordance with this Privacy 
                                Policy as required by applicable law.
                            </Text>

                            <Heading as="h3" size="sm" color={headingColor} mb={3}>
                                Analytics Services Provided by Others
                            </Heading>
                            <Text color={textColor} lineHeight="1.8" mb={3} fontSize={{ base: "sm", md: "md" }}>
                                Other parties may also provide analytics services via our Services. These analytics providers may set 
                                tracking technologies (like cookies) to collect information about your use of our Services and across 
                                other websites and online services. These technologies allow these third parties to recognize your device 
                                to compile information about you or others who use your device. This information allows us and other 
                                companies to, among other things, analyze and track usage. Please note this Privacy Policy only covers 
                                the collection of information by our Services and does not cover the collection of information by any 
                                third party advertisers or analytics providers.
                            </Text>

                            <Heading as="h3" size="sm" color={headingColor} mb={3}>
                                List of Sub-processors
                            </Heading>
                            <Text color={textColor} lineHeight="1.8" mb={2} fontSize={{ base: "sm", md: "md" }}>
                                We use the following products/services:
                            </Text>
                            <VStack spacing={2} align="stretch" pl={{ base: 3, md: 4 }}>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    • <strong>Google Cloud:</strong> for running servers and storing data
                                </Text>
                                <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                                    • <strong>Google Analytics:</strong> for analyzing the browsing behavior of our users and visitors
                                </Text>
                            </VStack>
                        </Box>

                        <Box>
                            <Heading as="h2" size={{ base: "sm", md: "md" }} color={headingColor} mb={4}>
                                Privacy Policy Changes
                            </Heading>
                            <Text color={textColor} lineHeight="1.8" fontSize={{ base: "sm", md: "md" }}>
                                Although most changes are likely to be minor, we may change our Privacy Policy from time to time, 
                                and in our sole discretion. We encourage visitors to frequently check this page for any changes 
                                to our Privacy Policy.
                            </Text>
                        </Box>
                    </VStack>
                </VStack>
        </Box>
    );
}
